// src/components/common/SearchFilterBar.js
import React from 'react';
import { Box, TextField, FormControl, InputLabel, Select, MenuItem, InputAdornment, IconButton, Tooltip, useTheme, alpha, InputBase, Paper } from '@mui/material';
import { Search as SearchIcon, FilterList as FilterListIcon, Refresh as RefreshIcon } from '@mui/icons-material';

const SearchFilterBar = ({ 
  searchTerm, 
  onSearchChange, 
  filterOptions = [], 
  filterValue, 
  onFilterChange, 
  onRefresh,
  placeholder = "Search...",
  filterLabel = "Filter",
  filterWidth = 120,
  showRefresh = true
}) => {
  const theme = useTheme();
  
  return (
    <Box display="flex" gap={1.5} flexWrap="wrap" alignItems="center">
      <Paper
        elevation={0}
        sx={{
          display: 'flex',
          alignItems: 'center',
          borderRadius: '10px',
          backgroundColor: alpha(theme.palette.background.paper, 0.8),
          border: `1px solid ${alpha(theme.palette.text.primary, 0.1)}`,
          pl: 1.5,
          flexGrow: 1,
          transition: 'all 0.2s',
          minWidth: '250px',
          maxWidth: '100%',
          '&:hover': {
            boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.08)}`,
            borderColor: alpha(theme.palette.primary.main, 0.3),
          },
        }}
      >
        <SearchIcon sx={{ color: alpha(theme.palette.text.secondary, 0.7), fontSize: 20, mr: 1 }} />
        <InputBase
          placeholder={placeholder}
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          sx={{
            flexGrow: 1,
            py: 1.2,
            px: 1,
            fontSize: '0.9rem',
            width: '100%',
            color: theme.palette.text.primary,
            '& ::placeholder': {
              color: alpha(theme.palette.text.secondary, 0.6),
              opacity: 1,
            },
          }}
        />
      </Paper>
      
      {filterOptions.length > 0 && (
        <FormControl
          size="small"
          sx={{
            minWidth: filterWidth,
          }}
        >
          <Select
            value={filterValue}
            displayEmpty
            renderValue={(selected) => selected || filterLabel}
            onChange={(e) => onFilterChange(e.target.value)}
            sx={{
              borderRadius: '10px',
              backgroundColor: alpha(theme.palette.background.paper, 0.8),
              border: `1px solid ${alpha(theme.palette.text.primary, 0.1)}`,
              height: '40px',
              fontSize: '0.875rem',
              pl: 1,
              transition: 'all 0.2s',
              '&:hover': {
                boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.08)}`,
                borderColor: alpha(theme.palette.primary.main, 0.3),
              },
              '&.Mui-focused': {
                borderColor: theme.palette.primary.main,
                boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`,
              },
              '& .MuiSelect-select': {
                paddingLeft: 0.5,
                display: 'flex',
                alignItems: 'center',
              },
            }}
            startAdornment={
              <InputAdornment position="start" sx={{ mr: 0.5 }}>
                <FilterListIcon fontSize="small" sx={{ color: alpha(theme.palette.text.secondary, 0.7) }} />
              </InputAdornment>
            }
            MenuProps={{
              PaperProps: {
                elevation: 3,
                sx: {
                  mt: 0.5,
                  borderRadius: '10px',
                  border: `1px solid ${alpha(theme.palette.text.primary, 0.1)}`,
                  boxShadow: `0 4px 20px ${alpha(theme.palette.common.black, 0.15)}`,
                },
              },
            }}
            IconComponent={(props) => (
              <FilterListIcon
                {...props}
                fontSize="small" 
                sx={{ 
                  color: alpha(theme.palette.text.secondary, 0.7),
                  right: 8
                }}
              />
            )}
          >
            <MenuItem value="">All {filterLabel}s</MenuItem>
            {filterOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label || option.value}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      
      {showRefresh && (
        <Tooltip title="Refresh">
          <IconButton 
            onClick={onRefresh}
            sx={{
              backgroundColor: alpha(theme.palette.primary.main, 0.1),
              color: theme.palette.primary.main,
              borderRadius: '10px',
              width: '40px',
              height: '40px',
              transition: 'all 0.2s',
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.2),
                transform: 'rotate(30deg)',
              },
            }}
          >
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
};

export default SearchFilterBar;